import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";

import { useSocket } from "@/helpers/hooks/useSocket";
import { logoutAction } from "@/features/auth/store/logoutAction";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import { SOCKET_CONST } from "@/config/constants";
import { SOCKET_URL_ADMIN } from "@/config/appConfig";

type BrandCoverageUpdate = {
  action?: string;
  data?: {
    title?: string;
    message?: string;
  };
};

type BrandCoverSocketProps = {
  brandId: string | number;
};

function BrandCoverSocket({ brandId }: BrandCoverSocketProps) {
  const { on, off } = useSocket(SOCKET_URL_ADMIN);
  const dispatch = useDispatch();

  const handleBrandCoverageUpdate = useCallback(
    (data?: BrandCoverageUpdate) => {
      const { action } = data;
      const {
        title = "Brand Coverage Updated",
        message = "The brand coverage settings have been updated by the admin, please login again!",
      } = data?.data || {};

      // need to check if reload can solve the issue instead of logout
      RpSweetAlert.fire({
        title: (
          <span style={{ fontWeight: 700, color: "var(--neutral-700)" }}>
            {title}
          </span>
        ),
        html: (
          <span style={{ fontWeight: 600, color: "var(--neutral-700)" }}>
            {message}
          </span>
        ),
        icon: "warning",
        showCancelButton: false,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          container: "z-top",
        },
      }).then((result: { dismiss?: unknown }) => {
        if (result.dismiss === RpSweetAlert?.DismissReason?.timer) {
          if (action === SOCKET_CONST.ACTION.LOGOUT) {
            dispatch(logoutAction() as never);
          } else if (action === SOCKET_CONST.ACTION.RELOAD) {
            if (typeof window !== undefined) {
              window.location.reload();
            }
          }
        }
      });
    },
    [dispatch]
  );

  useEffect(() => {
    const eventName = `brand-coverage-update-${brandId}`;

    // Register event listener
    on(eventName, handleBrandCoverageUpdate);

    // Cleanup function
    return () => {
      off(eventName);
    };
  }, [on, off, brandId, handleBrandCoverageUpdate]);

  return null;
}

export default BrandCoverSocket;
